import PropTypes from "prop-types";
import React from "react";
import { Alert, Button, Modal } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import * as styles from "./PersonalAccessTokensPageStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "personalAccessTokens.revoke.title",
		defaultMessage: "Revoke personal access token",
	},
	ariaLabel: {
		id: "personalAccessTokens.revoke.ariaLabel",
		defaultMessage: "Revoke a personal access token",
	},
	body: {
		id: "personalAccessTokens.revoke.body",
		defaultMessage: "You are about to revoke {tokenName}. Any script, pipeline or device using this token will immediately stop working.",
	},
	irreversible: {
		id: "personalAccessTokens.revoke.irreversible",
		defaultMessage: "This action cannot be undone.",
	},
	cancel: {
		id: "personalAccessTokens.revoke.cancel",
		defaultMessage: "Cancel",
	},
	confirm: {
		id: "personalAccessTokens.revoke.confirm",
		defaultMessage: "Revoke token",
	},
} );

/**
 * Confirms revoking a personal access token. The destructive action is
 * presented as the visual primary so the user knows what will happen if
 * they hit Enter.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function RevokeConfirmModal(
	{
		token = null,
		isRevoking = false,
		error = null,
		onCancel,
		onConfirm,
		intl,
	},
) {
	return (
		<Modal
			isOpen={ Boolean( token ) }
			onClose={ onCancel }
			role="alertdialog"
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<Modal.Title as="h2" size="2">
					<FormattedMessage { ...messages.title } />
				</Modal.Title>
				<div className={ styles.modalBody }>
					<p>
						<FormattedMessage
							{ ...messages.body }
							values={ {
								tokenName: <strong>&ldquo;{ token ? token.name : "" }&rdquo;</strong>,
							} }
						/>
					</p>
					<p className={ styles.revokeIrreversible }>
						<FormattedMessage { ...messages.irreversible } />
					</p>
					{ error && (
						<Alert variant="error">
							{ error }
						</Alert>
					) }
				</div>
				<div className={ styles.modalActions }>
					<Button variant="secondary" onClick={ onCancel } disabled={ isRevoking }>
						<FormattedMessage { ...messages.cancel } />
					</Button>
					<Button
						variant="error"
						onClick={ onConfirm }
						isLoading={ isRevoking }
						disabled={ isRevoking }
					>
						<FormattedMessage { ...messages.confirm } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

RevokeConfirmModal.propTypes = {
	token: PropTypes.shape( {
		id: PropTypes.string,
		name: PropTypes.string,
	} ),
	isRevoking: PropTypes.bool,
	error: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( RevokeConfirmModal );
